import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatMoney } from "../lib/constants";
import { ContactCombobox } from "../components/ContactCombobox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function Statement({ customerId, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/customers/${customerId}/statement`).then((r) => setData(r.data)).catch(() => {}); }, [customerId]);
  if (!data) return <div className="font-mono text-sm text-muted-foreground py-8">Loading statement…</div>;
  const cur = data.base_currency;
  return (
    <div data-testid="customer-statement">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-primary hover:underline mb-4" data-testid="statement-back"><ArrowLeft className="w-4 h-4" /> Back to Receivables</button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Statement of Account</div>
          <h2 className="font-display text-2xl font-black">{data.customer.name}</h2>
          {data.customer.email && <div className="text-sm text-muted-foreground">{data.customer.email}</div>}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Outstanding</div>
          <div className={`font-mono text-2xl font-bold ${data.outstanding > 0 ? "text-destructive" : "text-success"}`} data-testid="statement-outstanding">{formatMoney(data.outstanding, cur)}</div>
        </div>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary">
            <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Ref</TableHead><TableHead>Details</TableHead>
            <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.entries.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No transactions yet.</TableCell></TableRow>}
            {data.entries.map((e, i) => (
              <TableRow key={i} data-testid="statement-row">
                <TableCell className="font-mono text-sm">{e.date}</TableCell>
                <TableCell className="capitalize text-sm">{e.type}</TableCell>
                <TableCell className="font-mono text-xs">{e.ref}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.details}</TableCell>
                <TableCell className="text-right font-mono">{e.debit ? formatMoney(e.debit, cur) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-success">{e.credit ? formatMoney(e.credit, cur) : "—"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatMoney(e.balance, cur)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Receivables({ onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/receivables").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="font-mono text-sm text-muted-foreground py-8">Loading…</div>;
  const cur = data.base_currency;
  return (
    <div data-testid="receivables">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">Total Outstanding</div><div className="font-mono text-2xl font-bold text-destructive">{formatMoney(data.total_outstanding, cur)}</div></div>
        <div className="border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">Total Credit (deposits held)</div><div className="font-mono text-2xl font-bold text-success">{formatMoney(data.total_credit, cur)}</div></div>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary">
            <TableHead>Customer</TableHead><TableHead className="text-right">Invoiced</TableHead><TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Statement</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No customer balances yet.</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r.customer_id} className="hover:bg-secondary/60" data-testid="receivable-row">
                <TableCell className="font-medium">{r.name}<div className="text-xs text-muted-foreground">{r.phone}</div></TableCell>
                <TableCell className="text-right font-mono">{formatMoney(r.total_invoiced, cur)}</TableCell>
                <TableCell className="text-right font-mono">{formatMoney(r.total_paid, cur)}</TableCell>
                <TableCell className="text-right font-mono text-destructive">{r.outstanding ? formatMoney(r.outstanding, cur) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-success">{r.credit_balance ? formatMoney(r.credit_balance, cur) : "—"}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-none gap-1 h-8" onClick={() => onOpen(r.customer_id)} data-testid={`view-statement-${r.customer_id}`}><FileText className="w-3.5 h-3.5" />View</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Deposits() {
  const { user } = useAuth();
  const base = user.organization?.base_currency || "NGN";
  const empty = { customer_id: null, amount: 0, currency: base, date: new Date().toISOString().slice(0, 10), note: "" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const load = useCallback(() => api.get("/deposits").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    try { await api.post("/deposits", form); toast.success("Deposit recorded"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete deposit?")) return; await api.delete(`/deposits/${id}`); load(); };
  return (
    <div data-testid="deposits">
      <div className="flex justify-end mb-4">
        <Button className="rounded-none gap-2" onClick={() => { setForm(empty); setOpen(true); }} data-testid="create-deposit-btn"><Plus className="w-4 h-4" /> New Deposit</Button>
      </div>
      {items.length === 0 ? <EmptyState title="No deposits recorded" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid="deposit-row">
                  <TableCell className="font-mono text-sm">{d.date}</TableCell>
                  <TableCell className="font-medium">{d.customer_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.note}</TableCell>
                  <TableCell className="text-right font-mono">{formatMoney(d.amount, d.currency)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(d.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">Record Customer Deposit</DialogTitle><DialogDescription>Advance payment held as a credit on the customer's account.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Customer</Label><ContactCombobox type="customer" value={form.customer_id} onValueChange={(id) => setForm((f) => ({ ...f, customer_id: id }))} placeholder="Search or add a customer…" testId="deposit-customer" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value || 0) }))} data-testid="deposit-amount-input" /></div>
              <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            </div>
            <div><Label>Note</Label><Input className="rounded-none mt-1" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-deposit-btn">Save Deposit</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Accounts() {
  const [statementFor, setStatementFor] = useState(null);
  return (
    <div data-testid="accounts-page">
      <PageHeader title="Accounts" subtitle="Customer statements, receivables & deposits" />
      {statementFor ? (
        <Statement customerId={statementFor} onBack={() => setStatementFor(null)} />
      ) : (
        <Tabs defaultValue="receivables">
          <TabsList className="rounded-none">
            <TabsTrigger value="receivables" className="rounded-none" data-testid="tab-receivables">Receivables</TabsTrigger>
            <TabsTrigger value="deposits" className="rounded-none" data-testid="tab-deposits">Deposits</TabsTrigger>
          </TabsList>
          <TabsContent value="receivables" className="mt-6"><Receivables onOpen={setStatementFor} /></TabsContent>
          <TabsContent value="deposits" className="mt-6"><Deposits /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
